import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Sicherheiten from './Sicherheiten';
import Forderungen from './Forderungen';
import Loeschungen from './Loeschungen';
import Button from './ui/Button';
import Tabs from './ui/Tabs';
import './TransaktionDetails.css';
import { getAbteilungById } from '../services/apiService';

const TransaktionDetails = ({ onBack }) => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [entry, setEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Status-/Meldungszustand analog zu TransaktionAnlegen
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitMessage, setSubmitMessage] = useState('');
  const statusMessageRef = useRef(null);

  // Initiales Laden der Abteilung anhand der ID aus der URL
  useEffect(() => {
    const loadEntry = async () => {
      try {
        setIsLoading(true);
        const loadedEntry = await getAbteilungById(id);
        if (loadedEntry) {
          setEntry(loadedEntry);
        } else {
          setSubmitStatus('error');
          setSubmitMessage('Abteilung nicht gefunden.');
        }
      } catch (error) {
        console.error('Fehler beim Laden der Abteilung:', error);
        setSubmitStatus('error');
        setSubmitMessage(error.message || 'Fehler beim Laden der Daten.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadEntry();
    }
  }, [id]);

  // Automatisches Verbergen der Meldung nach 5 Sekunden
  useEffect(() => {
    if (submitStatus) {
      const timer = setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  // Automatisches Scrollen zur Meldung
  useEffect(() => {
    if (submitStatus && statusMessageRef.current) {
      statusMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitStatus]);

  // Handler für Aktualisierung nach Änderungen in den Tabs
  const handleUpdate = async (maybeErrorOrMessage) => {
    // Falls der aufrufende Tab (z. B. beim Löschen) einen Fehler liefert, direkt anzeigen
    if (maybeErrorOrMessage instanceof Error) {
      setSubmitStatus('error');
      setSubmitMessage(maybeErrorOrMessage.message || 'Es ist ein Fehler aufgetreten.');
      return;
    }

    try {
      // Lade die aktuelle Abteilung per ID neu
      const updatedEntry = await getAbteilungById(id);
      if (updatedEntry) {
        setEntry(updatedEntry);
      } else {
        // Abteilung existiert nicht mehr - direkt zurück zur Übersicht (ohne Erfolgsmeldung)
        onBack();
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Daten:', error);
      setSubmitStatus('error');
      setSubmitMessage(error.message || 'Fehler beim Aktualisieren der Daten.');
    }
  };

  if (isLoading) {
    return <div className="loading-container">Lade Transaktionsdaten...</div>;
  }

  if (!entry) {
    return (
      <div className="page-container">
        <div className="submit-status error" role="alert">
          {submitMessage || 'Transaktion nicht gefunden.'}
        </div>
        <Button variant="outline-primary" onClick={onBack}>
          zur Übersicht
        </Button>
      </div>
    );
  }

  const TABS = [
    { label: "Forderungen", component: Forderungen },
    { label: "Sicherheiten", component: Sicherheiten },
    { label: "Löschungen", component: Loeschungen },
  ];

  const ActiveComponent = TABS[activeTab].component;

  return (
    <div className="page-container">
      <h2 className="page-title">Details zur Abteilung Nr. {entry.nummer}</h2>

      {/* Statusmeldung (Erfolg / Fehler) - einheitliches Styling mit TransaktionAnlegen */}
      {submitStatus === 'error' && (
        <div className="submit-status error" role="alert" ref={statusMessageRef}>{submitMessage}</div>
      )}

      {/* Grunddaten */}
      <div className="top-block">
        <div className="top-block-row">
          <span className="top-block-label">Transaktion</span>
          <span className="top-block-value">{entry.transaktionsbezeichner || ''}</span>
        </div>
        <div className="top-block-row">
          <span className="top-block-label">Kommentare</span>
          <span className="top-block-value">{entry.kommentare || ''}</span>
        </div>
      </div>


      {/* Tabs Section */}
      <div className="middle-block">
        <Tabs 
          tabs={TABS} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />
        <div className="tab-content">
          <ActiveComponent entry={entry} onUpdate={handleUpdate} />
        </div>
      </div>

      {/* Actions Section */}
      <div className="bottom-block">
        <div className="bottom-block-right">
          <Button variant="outline-primary" ariaLabel="Zur Übersicht" title="Zur Übersicht" onClick={onBack}>
            zur Übersicht
          </Button>
        </div>
      </div>
    </div>
  );
};
export default TransaktionDetails;
